import type { CSSProperties } from 'react'

type UnknownRecord = Record<string, unknown>

export type WidgetThemeMode = 'light' | 'dark'
export type WidgetThemeModeSetting = WidgetThemeMode | 'auto'

export interface Rgb {
  r: number
  g: number
  b: number
}

function asRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as UnknownRecord
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function clampChannel(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(255, Math.max(0, Math.round(value)))
}

/**
 * Parses `#rgb`, `#rrggbb`, `#rrggbbaa` and `rgb()/rgba()` colour strings.
 * Returns null for anything we can't reason about, so callers fall back to the
 * default palette rather than emitting a broken custom property.
 */
export function parseColour(value: string | null | undefined): Rgb | null {
  if (!value) return null
  const raw = value.trim().toLowerCase()

  const hexMatch = /^#([0-9a-f]{3,8})$/.exec(raw)
  if (hexMatch) {
    const hex = hexMatch[1]
    if (hex.length === 3 || hex.length === 4) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      }
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      }
    }
    return null
  }

  const rgbMatch = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(raw)
  if (rgbMatch) {
    return {
      r: clampChannel(Number(rgbMatch[1])),
      g: clampChannel(Number(rgbMatch[2])),
      b: clampChannel(Number(rgbMatch[3])),
    }
  }

  return null
}

function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) => clampChannel(n).toString(16).padStart(2, '0')
  return `#${part(r)}${part(g)}${part(b)}`
}

function toRgba({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${clampChannel(r)}, ${clampChannel(g)}, ${clampChannel(b)}, ${alpha})`
}

/** WCAG relative luminance. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio between two colours (1 to 21). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 }
const NEAR_BLACK: Rgb = { r: 20, g: 24, b: 31 }

/**
 * Picks the foreground colour to place on top of `background`.
 *
 * Garages pick their brand colour without any thought for contrast — a light
 * yellow accent with white label text is unreadable. We ignore any configured
 * text colour that fails WCAG AA against the accent and choose the better of
 * white / near-black instead.
 */
export function pickAccessibleForeground(background: Rgb, preferred?: Rgb | null): string {
  if (preferred && contrastRatio(background, preferred) >= 4.5) {
    return toHex(preferred)
  }
  const onWhite = contrastRatio(background, WHITE)
  const onBlack = contrastRatio(background, NEAR_BLACK)
  return onWhite >= onBlack ? toHex(WHITE) : toHex(NEAR_BLACK)
}

function mix(colour: Rgb, target: Rgb, amount: number): Rgb {
  return {
    r: colour.r + (target.r - colour.r) * amount,
    g: colour.g + (target.g - colour.g) * amount,
    b: colour.b + (target.b - colour.b) * amount,
  }
}

/**
 * Hover state for a filled accent surface.
 *
 * The hover colour has to keep the same foreground readable — a hover that
 * lightens a mid-green until white label text drops below AA is a regression
 * you only notice with a mouse. So we move away from the foreground by
 * default, and only reverse direction when that move would be invisible
 * (a near-black accent cannot get meaningfully darker).
 */
export function deriveAccentHover(accent: Rgb, foreground: Rgb = WHITE): string {
  const foregroundIsLight = relativeLuminance(foreground) > 0.5
  const away = foregroundIsLight ? NEAR_BLACK : WHITE
  const towards = foregroundIsLight ? WHITE : NEAR_BLACK

  const preferred = mix(accent, away, 0.14)
  const isVisible = Math.abs(relativeLuminance(preferred) - relativeLuminance(accent)) >= 0.01

  if (isVisible && contrastRatio(preferred, foreground) >= AA_CONTRAST) {
    return toHex(preferred)
  }

  const fallback = mix(accent, towards, 0.14)
  if (contrastRatio(fallback, foreground) >= AA_CONTRAST) {
    return toHex(fallback)
  }

  return toHex(contrastRatio(preferred, foreground) >= AA_CONTRAST ? preferred : accent)
}

const AA_CONTRAST = 4.5

/**
 * Produces the colour actually used for filled, text-bearing surfaces
 * (primary buttons, badges).
 *
 * Picking only between white and near-black text is not enough: mid-tone
 * accents exist where neither reaches AA — our own default green
 * (#16a34a) is one, at 3.29:1 against white. So we keep the brand hue and
 * shift its lightness just far enough for the chosen foreground to clear
 * 4.5:1. The raw accent is still used for rings, borders and soft fills,
 * where contrast does not apply.
 */
export function deriveAccentSolid(accent: Rgb, preferred?: Rgb | null): { solid: Rgb; foreground: Rgb } {
  // A garage-supplied foreground wins outright if it already passes.
  if (preferred && contrastRatio(accent, preferred) >= AA_CONTRAST) {
    return { solid: accent, foreground: preferred }
  }

  // Choose by convention rather than by whichever scores higher: a mid-green
  // brand button is expected to carry white text, even though near-black
  // would technically score better. Only genuinely light accents (yellows,
  // creams, pastels) read as dark-on-light.
  const LIGHT_ACCENT_LUMINANCE = 0.45
  const foreground = relativeLuminance(accent) > LIGHT_ACCENT_LUMINANCE ? NEAR_BLACK : WHITE

  if (contrastRatio(accent, foreground) >= AA_CONTRAST) {
    return { solid: accent, foreground }
  }

  // Move the accent away from its foreground, preserving hue: darken under
  // white text, lighten under dark text.
  const target = relativeLuminance(foreground) > 0.5 ? NEAR_BLACK : WHITE

  let solid = accent
  for (let step = 1; step <= 20; step++) {
    solid = mix(accent, target, step * 0.05)
    if (contrastRatio(solid, foreground) >= AA_CONTRAST) break
  }

  return { solid, foreground }
}

/**
 * The surfaces each palette in tokens.css paints, in token order: surface,
 * raised, sunken, hover, active. Mirrored here because accent text has to be
 * checked against every one of them; a test keeps the lists in step with the
 * stylesheet.
 */
export const MODE_SURFACES: Record<WidgetThemeMode, readonly string[]> = {
  dark: ['#101418', '#171d23', '#0a0d10', '#1f272f', '#262f39'],
  light: ['#ffffff', '#ffffff', '#f2f4f7', '#eceff3', '#e2e7ec'],
}

/** Tints are laid on the resting surfaces (surface, raised, sunken), never on a hover. */
const TINTED_SURFACE_COUNT = 3

/** Alpha of the accent tint (--ac-accent-soft) laid over a surface. */
export const ACCENT_SOFT_ALPHA = 0.14

/** The colour an rgba() fill actually shows once laid over `surface`. */
export function composite(fill: Rgb, alpha: number, surface: Rgb): Rgb {
  return mix(surface, fill, alpha)
}

/**
 * Every background accent-coloured text can sit on in `mode`: each surface,
 * and each surface under the accent's own soft tint (selected rows, tags).
 */
export function accentTextBackgrounds(accent: Rgb, mode: WidgetThemeMode): Rgb[] {
  const surfaces = MODE_SURFACES[mode].map((value) => parseColour(value) as Rgb)
  const tinted = surfaces
    .slice(0, TINTED_SURFACE_COUNT)
    .map((surface) => composite(accent, ACCENT_SOFT_ALPHA, surface))
  return [...surfaces, ...tinted]
}

/**
 * The accent as text, links, icons and focus rings on the panel's own
 * surfaces.
 *
 * Filled buttons already have --ac-accent-solid, but that colour is tuned for
 * a label sitting on it, which pulls it the wrong way for text on a dark panel
 * (our green dropped to 3.3:1 on the dark selected card). This keeps the brand
 * hue and moves its lightness, towards white on the dark palette and towards
 * near-black on the light one, until it clears 4.5:1 on every surface and tint.
 */
export function deriveAccentText(accent: Rgb, mode: WidgetThemeMode): Rgb {
  const target = mode === 'dark' ? WHITE : NEAR_BLACK
  const backgrounds = accentTextBackgrounds(accent, mode)
  const worst = (colour: Rgb) =>
    Math.min(...backgrounds.map((background) => contrastRatio(colour, background)))

  if (worst(accent) >= AA_CONTRAST) return accent

  for (let step = 1; step <= 40; step++) {
    // Rounded before it is judged: the hex written out is what gets drawn,
    // and rounding alone can cost the last hundredth of a ratio.
    const candidate = parseColour(toHex(mix(accent, target, step * 0.025))) as Rgb
    if (worst(candidate) >= AA_CONTRAST) return candidate
  }
  return target
}

export interface WidgetThemeTokens {
  accent: string
  accentSolid: string
  accentHover: string
  accentContrast: string
  accentText: string
  accentSoft: string
  accentBorder: string
  focusRing: string
}

/** Derives the full accent token set from a single brand colour. */
export function deriveAccentTokens(
  accentValue: string,
  preferredForeground?: string | null,
  mode: WidgetThemeMode = 'dark'
): WidgetThemeTokens | null {
  const accent = parseColour(accentValue)
  if (!accent) return null

  const { solid, foreground } = deriveAccentSolid(accent, parseColour(preferredForeground))

  return {
    accent: toHex(accent),
    accentSolid: toHex(solid),
    accentHover: deriveAccentHover(solid, foreground),
    accentContrast: toHex(foreground),
    accentText: toHex(deriveAccentText(accent, mode)),
    accentSoft: toRgba(accent, ACCENT_SOFT_ALPHA),
    accentBorder: toRgba(accent, 0.45),
    focusRing: toRgba(accent, 0.55),
  }
}

/**
 * Builds the inline custom properties applied to the widget root from
 * `channel.themeJson`.
 *
 * Supported shape:
 * {
 *   mainThemeColour: "#16a34a",
 *   mainTextColour: "#111827",
 *   mainThemeMode: "light" | "dark" | "auto"
 * }
 *
 * Any value that is null, undefined, empty or unparseable is ignored and the
 * token keeps its default from tokens.css.
 */
export function buildWidgetThemeStyle(
  themeJson: unknown,
  mode: WidgetThemeMode = getWidgetThemeMode(themeJson)
): CSSProperties {
  const root = asRecord(themeJson)
  if (!root) return {}

  const accentValue = nonEmptyString(root.mainThemeColour)
  if (!accentValue) return {}

  const tokens = deriveAccentTokens(accentValue, nonEmptyString(root.mainTextColour), mode)
  if (!tokens) return {}

  const style: Record<string, string> = {
    '--ac-accent': tokens.accent,
    '--ac-accent-solid': tokens.accentSolid,
    '--ac-accent-hover': tokens.accentHover,
    '--ac-accent-contrast': tokens.accentContrast,
    '--ac-accent-text': tokens.accentText,
    '--ac-accent-soft': tokens.accentSoft,
    '--ac-accent-border': tokens.accentBorder,
    '--ac-focus-ring': tokens.focusRing,
  }

  return style as CSSProperties
}

/** Reads the configured mode, including the `auto` setting. */
export function getWidgetThemeModeSetting(themeJson: unknown): WidgetThemeModeSetting | null {
  const root = asRecord(themeJson)
  if (!root) return null

  const modeRaw = nonEmptyString(root.mainThemeMode)
  if (!modeRaw) return null

  const mode = modeRaw.toLowerCase()
  if (mode === 'light' || mode === 'dark' || mode === 'auto') return mode
  return null
}

function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Resolves the mode actually applied to the DOM.
 *
 * Dark is the default. The widget's design is dark-first — a garage that has
 * not expressed a preference gets the intended look, and light is an explicit
 * opt-in for sites it would clash with.
 */
export function getWidgetThemeMode(themeJson: unknown): WidgetThemeMode {
  const setting = getWidgetThemeModeSetting(themeJson)
  if (setting === 'light') return 'light'
  if (setting === 'dark') return 'dark'
  if (setting === 'auto') return prefersDarkScheme() ? 'dark' : 'light'
  return 'dark'
}
